import { AxiosError, AxiosResponse } from 'axios'
import { camelize } from 'casing'
import { DateTime } from 'luxon'
import { isNull, isUndefined } from 'lodash'
import { PlanType } from '@labrav/react-components'
import {
  getErrorMessages,
  isNonEmptyNumber,
  RequestFailureMessage,
  updateAppAlert,
} from '@labrav/common'

import {
  patchAzurePrivateOffersData,
  postAzurePrivateOffer,
} from '../../api/azure/index'
import { putGCPPrivateOffersData } from '../../api/gcp/index'
import {
  getSingleChannelOffer,
  getSinglePrivateOffer,
  patchPrivateOffersData,
  postPrivateOffer,
} from '../../api/marketplace/index'
import type { AppDispatch } from '../../store/types'
import type { CloudMarketplace } from '../../types/index'
import type { Product } from '../productsListing/types'
import type { UserState } from '../../types/external'
import type { ResaleAuthorizationsResellerAgreements } from '../../types/resaleAuthorization'
import {
  CppoDimension,
  CppoPriceAdjustmentType,
  Dimension,
  DiscountType,
  OfferStakeholders,
} from '../../types/flyout'
import { getMarkupValues } from '../../utils/channelOfferUtil'
import { getMappedMonthKey } from '../../utils/contractDuration'

export { getMappedMonthKey }

export const convertDateFromAPIResponse = (date?: string) => {
  return date
    ? DateTime.fromISO(date).toFormat('yyyy-MM-dd').toLocaleString()
    : null
}

export const getUpdatedDimensionsForDraftPO = (
  dimensions: Dimension[],
  productId: string
) => {
  return dimensions.map((dimension: Dimension) => {
    const { id, createdAt, ...remainingDimension } = dimension
    remainingDimension.awsProductId = productId
    return remainingDimension
  })
}

export const returnOnlySpecificDimensionsField = (
  dimensions: Dimension[],
  selectedCurrency?: string
) => {
  return dimensions.map(
    ({
      type,
      awsProductId,
      createdAt,
      crmObjectId,
      dimensionId,
      isAdditionalUsageAllowed,
      labelDescription,
      label,
      name,
      price,
      quantity,
    }) => ({
      currency: selectedCurrency,
      type,
      awsProductId,
      createdAt,
      crmObjectId,
      dimensionId,
      isAdditionalUsageAllowed,
      labelDescription,
      label,
      name,
      price,
      quantity,
    })
  )
}

export const getPrivateOfferDataFromAPIResponse = async (
  cloud: CloudMarketplace,
  {
    partnerId,
    privateOfferId,
    crmOpportunityId,
    productId,
    product,
  }: {
    productId: string
    partnerId: string
    privateOfferId: string
    crmOpportunityId?: string
    product?: Product
  },
  dispatch: AppDispatch
) => {
  try {
    switch (cloud) {
      case 'AWS': {
        const { data } = await getSinglePrivateOffer(
          partnerId,
          privateOfferId,
          crmOpportunityId
        )
        const { productName, links, ...remainingData } = camelize(
          data || {}
        ) as Record<string, unknown> & {
          dimensions?: Dimension[]
          offerExpirationDate?: string
          subscriptionEndDate?: string
          agreementStartDate?: string
        }

        return {
          ...remainingData,
          awsProductId: productId,
          offerExpirationDate: convertDateFromAPIResponse(
            remainingData?.offerExpirationDate
          ),
          ...(product?.productType === PlanType.SAAS_SUBSCRIPTION
            ? {
                subscriptionEndDate: convertDateFromAPIResponse(
                  remainingData?.subscriptionEndDate
                ),
              }
            : {}),
          ...(product?.productType === PlanType.SAAS_CONTRACT
            ? {
                agreementStartDate: convertDateFromAPIResponse(
                  remainingData?.agreementStartDate
                ),
              }
            : {}),
          dimensions: getUpdatedDimensionsForDraftPO(
            remainingData.dimensions || [],
            productId
          ),
        }
      }
      case 'AZURE':
      case 'GCP':
      default:
        return {}
    }
  } catch (err: unknown) {
    const error = err as AxiosError
    dispatch(
      updateAppAlert({
        message: getErrorMessages([RequestFailureMessage])(
          error.response as AxiosResponse<ErrorResponse & { message?: string }>
        ),
        messageType: 'ERROR',
        autoClose: true,
      })
    )
    return {}
  }
}

export const postOfferBasedOnCloud = async (
  cloud: CloudMarketplace,
  OfferPayload: {
    partnerId: string
    data: Record<string, unknown>
    saveAsDraft?: boolean
  }
) => {
  switch (cloud) {
    case 'AWS':
      return await postPrivateOffer(OfferPayload)
    case 'AZURE':
      return await postAzurePrivateOffer(OfferPayload)
    case 'GCP':
      return await putGCPPrivateOffersData(OfferPayload)
    default:
      return undefined
  }
}

export const patchOfferBasedOnCloud = async (
  cloud: CloudMarketplace,
  OfferPayload: {
    partnerId: string
    data: Record<string, unknown>
    saveAsDraft?: boolean
    privateOfferId: string
  }
) => {
  const { partnerId, data, saveAsDraft, privateOfferId } = OfferPayload
  switch (cloud) {
    case 'AWS':
      return await patchPrivateOffersData(
        partnerId,
        privateOfferId,
        data,
        saveAsDraft
      )
    case 'AZURE':
      return await patchAzurePrivateOffersData(
        partnerId,
        privateOfferId,
        data,
        saveAsDraft
      )
    case 'GCP':
      return await putGCPPrivateOffersData({
        data,
        partnerId,
      })
    default:
      return undefined
  }
}

const getAdjustedPercentageValue = ({
  dimensions,
  priceAdjustmentType,
  adjustmentPercentage,
  resaleAuthorization,
}: {
  dimensions?: CppoDimension[]
  priceAdjustmentType: CppoPriceAdjustmentType
  adjustmentPercentage?: number | null
  resaleAuthorization?: ResaleAuthorizationsResellerAgreements
}) => {
  if (priceAdjustmentType === CppoPriceAdjustmentType.PERCENTAGE) {
    return adjustmentPercentage
  }

  if (isNull(adjustmentPercentage) || isUndefined(adjustmentPercentage)) {
    return null
  }

  if (!dimensions || !dimensions?.length) return null

  const { totalDueToIsv } = getMarkupValues({
    dimensions,
    adjustmentPercentage: adjustmentPercentage ?? undefined,
    priceAdjustmentType,
    resaleAuthorization,
  })

  if (!isNonEmptyNumber(totalDueToIsv)) return 0

  const adjustmentPercentageValue =
    totalDueToIsv! + totalDueToIsv! * (adjustmentPercentage / 100)
  return adjustmentPercentageValue
}

export const getSingleChannelOfferBasedOnCloud = async ({
  cloudMarketplace,
  partnerId,
  channelOfferId,
  receivedResellerAuth,
  allUsers,
}: {
  cloudMarketplace: CloudMarketplace
  partnerId: string
  channelOfferId: string
  receivedResellerAuth?: ResaleAuthorizationsResellerAgreements
  allUsers?: UserState[]
}) => {
  switch (cloudMarketplace) {
    case 'AWS': {
      const { data } = await getSingleChannelOffer(partnerId, channelOfferId)
      const responseData = data as {
        aws_cppos?: Array<Record<string, unknown>>
      }
      if (responseData?.aws_cppos && responseData?.aws_cppos?.length) {
        const {
          installments,
          customMetaData,
          adjustmentPercentage,
          subscriptionEndDate,
          agreementStartDate,
          offerExpirationDate,
          dimensions,
          offerStakeholders,
          flexiblePaymentTerms,
          ...otherOfferData
        } = camelize(responseData.aws_cppos[0]) as Record<string, unknown> & {
          installments?: Array<{ paymentDate?: string }>
          customMetaData?: unknown[]
          adjustmentPercentage?: number | null
          subscriptionEndDate?: string
          agreementStartDate?: string
          offerExpirationDate?: string
          dimensions?: CppoDimension[]
          offerStakeholders?: OfferStakeholders[]
          flexiblePaymentTerms?: boolean
          priceAdjustmentType?: CppoPriceAdjustmentType
        }

        const allUsersEmail = (allUsers || [])?.map(item => item?.email)
        const offerStakeholdersData = offerStakeholders?.filter(
          (item: OfferStakeholders) => allUsersEmail.includes(item?.email)
        )
        const customOfferStakeholdersData = offerStakeholders?.filter(
          (item: OfferStakeholders) => !allUsersEmail.includes(item?.email)
        )

        const dimensionsList = (dimensions || [])
          ?.sort(
            (firstDimension: CppoDimension, secondDimension: CppoDimension) => {
              if (
                firstDimension?.isAdditionalUsageAllowed ===
                secondDimension?.isAdditionalUsageAllowed
              )
                return 0
              return firstDimension.isAdditionalUsageAllowed ? 1 : -1
            }
          )
          .map((dimensionItem: CppoDimension & { customPrice?: unknown }) => {
            const { customPrice, ...remainingDimension } = dimensionItem
            return {
              ...remainingDimension,
              price: customPrice,
            }
          })

        const adjustmentPercentageValue =
          otherOfferData?.priceAdjustmentType ===
          CppoPriceAdjustmentType.PERCENTAGE
            ? adjustmentPercentage
            : getAdjustedPercentageValue({
                priceAdjustmentType: otherOfferData?.priceAdjustmentType!,
                adjustmentPercentage,
                dimensions: dimensionsList as CppoDimension[],
                resaleAuthorization: receivedResellerAuth,
              })
        const channelOfferData = {
          ...otherOfferData,
          adjustmentPercentage: adjustmentPercentageValue,
          installments,
          metaData: (customMetaData || [])?.length
            ? customMetaData
            : [{ key: '', value: '' }],
          flexiblePaymentTerms:
            receivedResellerAuth?.discountType ===
            DiscountType.FLEXIBLE_PAYMENT_TERMS
              ? true
              : flexiblePaymentTerms,
          offerStakeholders: allUsersEmail
            ? offerStakeholdersData
            : offerStakeholders,
          customOfferStakeholders: customOfferStakeholdersData,
          adjustmentPercentageInputValue: adjustmentPercentageValue,
          offerExpirationDate: offerExpirationDate
            ? convertDateFromAPIResponse(offerExpirationDate)
            : offerExpirationDate,
          agreementStartDate: agreementStartDate
            ? convertDateFromAPIResponse(agreementStartDate)
            : agreementStartDate,
          subscriptionEndDate: subscriptionEndDate
            ? convertDateFromAPIResponse(subscriptionEndDate)
            : subscriptionEndDate,
          dimensions: dimensionsList,
          firstInstallmentDate: installments?.length
            ? installments[0].paymentDate
            : null,
        }
        return channelOfferData
      }
      return {}
    }
    default:
      return {}
  }
}
